import { z } from "zod";

/**
 * Who finished where — one rule for every place a rank is shown: the stage, each phone,
 * the presenter's results, the report and the spreadsheet. With prizes on the line, the
 * tenth place on the big screen and the tenth row of the download must be the same
 * person.
 *
 * More points first. Equal points go to whoever took less time over the answers that
 * scored — counted in hundredths of a second, so a screen and a database that each add
 * them up get the same total. Equal on both, and the place is shared: 1, 1, 3.
 */

/** A response time as the tie-break counts it: whole hundredths of a second. */
export const tiebreakUnits = (responseMs: number) => Math.max(0, Math.floor(responseMs / 10));

/**
 * The most tie-break time anyone can carry: 27 hours, far beyond any session. Capped so
 * the live board can fold points and time into one number without either spilling into
 * the other (round-store.ts).
 */
export const TIEBREAK_LIMIT = 10_000_000 - 1;

export type Standing = { score: number; /** In `tiebreakUnits`, capped at TIEBREAK_LIMIT. */ time: number };

/** Ahead, behind or level, by the rule above. */
export function compareStandings(a: Standing, b: Standing): number {
  return b.score - a.score || Math.min(a.time, TIEBREAK_LIMIT) - Math.min(b.time, TIEBREAK_LIMIT);
}

/** Best first, each with their place: one more than the number strictly ahead of them. */
export function rankStandings<T extends Standing>(rows: readonly T[]): Array<T & { rank: number }> {
  const sorted = [...rows].sort(compareStandings);
  let rank = 0;
  return sorted.map((row, i) => {
    if (i === 0 || compareStandings(sorted[i - 1]!, row) !== 0) rank = i + 1;
    return { ...row, rank };
  });
}

/**
 * The code on a participant's phone that proves who they are to whoever hands out the
 * prizes: a nickname anyone could claim, this only the phone holds. Five characters with
 * nothing to misread — no 0 and O, no 1, I and L.
 */
export const CLAIM_CODE_ALPHABET = "ACDEFGHJKMNPQRTUVWXY3479";
export const CLAIM_CODE_LENGTH = 5;
export const claimCodeSchema = z.string().regex(new RegExp(`^[${CLAIM_CODE_ALPHABET}]{${CLAIM_CODE_LENGTH}}$`));
